import {
  useState,
  MouseEvent,
  MouseEventHandler,
  KeyboardEventHandler,
} from 'react';
import { observer } from 'mobx-react-lite';
import { useTheme } from '@mui/material';
import AuthStore from '@/stores/AuthStore';
import {
  TextArea,
  TextareaInputEventType,
} from '@/domains/survey/components/form/TextArea';
import { Button } from '@/components/Button/Button';
import CustomAvatar from '@/components/CustomAvatar';
import { loggedActionsGuard } from '@/utils/loggedActionsGuard';
import { CommentType } from '@/types/Content/Comment';
import { useCommentStore } from '../CommentContext';
import { ActionsBar, Container, FormContainer } from './CommentForm.styles';

function CommentForm({
  onComment,
  onCancelClick,
  parentId,
  grayInput,
}: {
  onComment?: (comment: CommentType) => void;
  onCancelClick?: () => void;
  parentId?: string;
  grayInput?: boolean;
}): JSX.Element {
  const theme = useTheme();
  const [commentingEnabled, setCommentingEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [commentText, setCommentText] = useState<string>('');
  const { isLoggedIn, cmsUser } = AuthStore;

  const isReplies = Boolean(parentId);

  const { create, createReply, contentId } = useCommentStore();
  const verifyAuth = (loggedActionsGuard(isLoggedIn, false, () =>
    setCommentingEnabled(true),
  ) as unknown) as MouseEventHandler;

  const handleChange = (event: TextareaInputEventType): void => {
    if (commentingEnabled) {
      setCommentText(event.target.value);
    }
  };

  const createComment = async () => {
    if (commentText && cmsUser) {
      setIsLoading(true);

      const data = {
        body: commentText,
        user: cmsUser,
      };

      if (isReplies) {
        await createReply(data, { parentId });
        // TODO do we need onReply?
      } else {
        const newComment = await create(data);
        if (onComment && newComment) {
          onComment(newComment);
        }
      }

      setCommentText('');
      setIsLoading(false);
    }
  };

  const onClickHandler = (loggedActionsGuard(
    isLoggedIn,
    false,
    (event?: MouseEvent) => {
      event?.stopPropagation();
      createComment();
    },
  ) as unknown) as (event?: MouseEvent) => void;

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    if (event.metaKey || event.ctrlKey) {
      if (event.code === 'Enter') {
        onClickHandler();
      }
    }
  };

  return (
    <Container onClick={verifyAuth} replies={isReplies}>
      <CustomAvatar
        name={AuthStore?.cmsUser?.firstName}
        image={AuthStore?.cmsUser?.image?.url}
        size={{ desktop: 11.5, mobile: 11.5 }}
        fontSize={{ desktop: 6, mobile: 5 }}
      />
      <FormContainer>
        <TextArea
          id={contentId}
          name="comment"
          value={commentText}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          initialHeight={theme.spacing(11.5)}
          paddingY={theme.spacing(2)}
          fontSize={theme.spacing(4.5)}
          borderRadius={theme.spacing(6)}
          placeholder={`Write a ${isReplies ? 'reply' : 'comment'}...`}
          grayInput={grayInput}
        />
        <ActionsBar>
          <Button onClick={onClickHandler} loading={isLoading} small>
            Save
          </Button>
          <Button
            variant="outlined"
            onClick={onCancelClick}
            disabled={isLoading}
            small
          >
            Cancel
          </Button>
        </ActionsBar>
      </FormContainer>
    </Container>
  );
}
CommentForm.defaultProps = {
  onComment: null,
};

export default observer(CommentForm);
